/**
 * Pletsi redesign helpers.
 * 1. Gentle fade-in for page sections as they scroll into view. Only the section *content*
 *    fades; section backgrounds stay painted, so fast scrolling never shows gaps.
 * 2. Product cards: the theme jumps to the second photo on hover; we replace that with a
 *    soft crossfade (styles in pletsi.css, .pletsi-hover-img).
 * Both are skipped in the theme editor. Content is never hidden before this script runs:
 * the hidden state is a class it adds itself, only to sections below the first screen.
 */
(() => {
  const designMode = document.documentElement.classList.contains('shopify-design-mode');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** @param {() => void} callback */
  const onReady = (callback) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback);
    else callback();
  };

  /* ---- 1. Section fade-in ---- */
  if (!designMode && !reduceMotion && 'IntersectionObserver' in window) {
    /** @type {Set<Element>} */
    const pending = new Set();

    /** @param {Element} section */
    const reveal = (section) => {
      section.classList.remove('pletsi-reveal--hidden');
      // Drop the transition hook once the 0.8s fade has played, so it never lingers on
      // the theme's own elements (their transitions, transforms and fixed children).
      setTimeout(() => section.classList.remove('pletsi-reveal'), 1000);
    };

    // No negative bottom margin: a short last section (footer utilities) would never intersect.
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        pending.delete(entry.target);
        reveal(entry.target);
      }
      if (!pending.size) observer.disconnect();
    });

    for (const section of document.querySelectorAll('#MainContent > .shopify-section, footer > .shopify-section')) {
      if (section.getBoundingClientRect().top < window.innerHeight) continue;
      section.classList.add('pletsi-reveal', 'pletsi-reveal--hidden');
      pending.add(section);
      observer.observe(section);
    }

    // Printing doesn't scroll, so show whatever is still waiting.
    window.addEventListener('beforeprint', () => {
      for (const section of pending) reveal(section);
      pending.clear();
      observer.disconnect();
    });
  }

  /* ---- 2. Product card hover crossfade ---- */
  const GALLERY = '.card-gallery[on\\:pointerenter]';

  /** @param {Element} gallery */
  const enhanceGallery = (gallery) => {
    // Hidden slides are extra variant images; the theme's hover skips them too.
    const slides = gallery.querySelectorAll('slideshow-slide:not([hidden])');
    const first = slides[0];
    const secondImg = slides[1]?.querySelector('img');
    if (!first || !secondImg) return;

    // Stop the theme's instant slide swap; our overlay fades in instead.
    gallery.removeAttribute('on:pointerenter');
    gallery.removeAttribute('on:pointerleave');

    const overlay = /** @type {HTMLImageElement} */ (secondImg.cloneNode());
    // Keep the copy out of the card's refs (e.g. imagesToTransition[] for the view transition).
    overlay.removeAttribute('ref');
    overlay.removeAttribute('id');
    overlay.removeAttribute('fetchpriority');
    overlay.className = 'pletsi-hover-img';
    overlay.alt = '';
    overlay.setAttribute('aria-hidden', 'true');
    first.querySelector('.pletsi-hover-img')?.remove();
    (first.querySelector('.product-media') || first).append(overlay);
  };

  /** @param {Element} root */
  const enhanceCards = (root) => {
    if (root.matches(GALLERY)) enhanceGallery(root);
    for (const gallery of root.querySelectorAll(GALLERY)) enhanceGallery(gallery);
  };

  if (!designMode) {
    onReady(() => {
      enhanceCards(document.body);
      // Cards added later (filters, pagination, recommendations), and cards whose hover
      // handlers a section re-render put back.
      new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes') {
            if (m.target instanceof Element && m.target.matches(GALLERY)) enhanceGallery(m.target);
            continue;
          }
          for (const node of m.addedNodes) {
            if (node instanceof Element) enhanceCards(node);
          }
        }
      }).observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['on:pointerenter'],
      });
    });
  }

  /* ---- 3. FAQ accordion: smooth open / close (height + fade), one answer open at a time ---- */
  const faqDuration = reduceMotion ? 0 : 320;

  /**
   * @param {HTMLDetailsElement} item
   * @param {HTMLElement} panel
   * @param {boolean} closing
   */
  const animateFaq = (item, panel, closing) => {
    item.dataset.animating = '1';
    const easing = 'cubic-bezier(0.4, 0, 0.2, 1)';

    if (!closing) item.open = true;
    const h = panel.scrollHeight;
    if (closing) item.classList.add('is-closing');

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (closing) {
        item.open = false;
        item.classList.remove('is-closing');
      }
      delete item.dataset.animating;
    };
    const frames = [
      { height: '0px', opacity: 0 },
      { height: h + 'px', opacity: 1 },
    ];
    panel
      .animate(closing ? frames.reverse() : frames, { duration: faqDuration, easing })
      .finished.then(finish, finish);
    setTimeout(finish, faqDuration + 150); // safety net if the animation never reports back
  };

  document.addEventListener('click', (event) => {
    if (!faqDuration) return;
    const summary = event.target instanceof Element ? event.target.closest('.pletsi-faq__summary') : null;
    const item = summary?.parentElement;
    if (!(item instanceof HTMLDetailsElement)) return;
    const panel = item.querySelector('.pletsi-faq__answer');
    const group = item.closest('.pletsi-faq');
    if (!(panel instanceof HTMLElement) || !group) return;
    event.preventDefault();
    if (group.querySelector('.pletsi-faq__item[data-animating]')) return;
    const closing = item.open;

    if (!closing) {
      for (const other of group.querySelectorAll('details.pletsi-faq__item')) {
        // the native name="" group would snap the open row shut; from here the script closes it
        other.removeAttribute('name');
        const otherPanel = other.querySelector('.pletsi-faq__answer');
        if (other !== item && other instanceof HTMLDetailsElement && other.open && otherPanel instanceof HTMLElement) {
          animateFaq(other, otherPanel, true);
        }
      }
    }
    animateFaq(item, panel, closing);
  });

  /* ---- 4. FAQ: drop list bullets left empty after images were removed ---- */
  onReady(() => {
    for (const li of document.querySelectorAll('.pletsi-faq__answer li')) {
      if (!li.textContent.replace(/[\s ]+/g, '')) li.remove();
    }
  });

  /* ---- 5. Shopify's automatic all-products collection is called "Products": show it in Croatian ---- */
  const main = document.getElementById('MainContent');
  if (main && main.dataset.handle === 'all') {
    for (const h of main.querySelectorAll('h1, [id$="__section"] span')) {
      if (h.textContent.trim() === 'Products') h.textContent = 'Sve torbe';
    }
    document.title = document.title.replace(/^Products/, 'Sve torbe');
  }
})();

/* focus ring hidden after mouse / touch; any non-modifier key (Tab, arrows, Enter…) brings it back */
(function () {
  var root = document.documentElement;
  var modifiers = ['Shift', 'Control', 'Alt', 'Meta'];
  document.addEventListener('keydown', function (e) {
    if (modifiers.indexOf(e.key) === -1) root.classList.remove('pletsi-pointer');
  }, true);
  document.addEventListener('pointerdown', function () {
    root.classList.add('pletsi-pointer');
  }, true);
})();

/* text pages + policies: drop blank spacer paragraphs (&nbsp;) and empty headings
   (e.g. <h3><b></b><br></h3>) that leave uneven gaps or a blank section with a line */
(function () {
  function clean() {
    document.querySelectorAll('#MainContent :is(.page-content, .rte, .shopify-policy__body) :is(p, h2, h3, h4, h5, h6)').forEach(function (el) {
      if (el.closest('.pletsi-faq') || el.textContent.replace(/\s/g, '')) return;
      if (!el.querySelector(el.tagName === 'P' ? 'img, iframe, video, svg' : 'img, svg')) el.style.display = 'none';
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', clean); else clean();
})();

/* leftover English that comes from store data / apps, not the theme */
(function () {
  var map = {
    'Gift cards': 'Poklon bonovi',
    'Gift Cards': 'Poklon bonovi',
    'GIFT CARDS': 'Poklon bonovi',
    'Shipping options': 'Načini dostave',
    'Please select shipping option first': 'Najprije odaberite način dostave',
    'Privacy policy': 'Politika privatnosti',
    'Refund policy': 'Povrat i zamjena',
    'Terms of service': 'Uvjeti korištenja',
    'Contact information': 'Kontakt podaci'
  };
  var titles = {
    'Privacy policy': 'Politika privatnosti', 'Refund policy': 'Povrat i zamjena',
    'Terms of service': 'Uvjeti korištenja', 'Contact information': 'Kontakt podaci',
    'Your Shopping Cart': 'Košarica', 'Collections': 'Kolekcije', 'Search': 'Pretraživanje',
    '404 Not Found': 'Stranica nije pronađena', 'Account': 'Račun', 'Create Account': 'Izrada računa'
  };
  function fix(root) {
    var w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT), n, list = [];
    while ((n = w.nextNode())) list.push(n);
    list.forEach(function (t) {
      var v = t.nodeValue.trim();
      if (map[v] && !t.parentElement.closest('script, style')) t.nodeValue = t.nodeValue.replace(v, map[v]);
    });
  }
  function run() {
    fix(document.body);
    var t = document.title.split(' – ')[0].split(':')[0].trim();
    if (titles[t]) document.title = document.title.replace(t, titles[t]);
    new MutationObserver(function (ms) {
      ms.forEach(function (m) { m.addedNodes.forEach(function (a) { if (a.nodeType === 1) fix(a); else if (a.nodeType === 3 && a.parentElement) fix(a.parentElement); }); });
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();
